import React from "react"

export const data = [
  {
    id: "Website_Development",
    title: "Copywriting",
    subtitle:
      "If you don't spend on ads now, just test out a number, 10% of your total marketing budget is a good place to start. Once you've entered all other metrics, come back to budget to see how it affects profit.",
    heading: "Projected Monthly Budget",
    formula: ({
      pages,
      copywriting,
      customWebGraphics,
      animatedGraphics,
      customWork,
    }) => {
      const basePrice = 2000
      const result =
        basePrice +
        pages * 2000 +
        pages * copywriting +
        customWebGraphics * pages +
        animatedGraphics * pages +
        customWork * pages

      return result
    },
    inputs: [
      {
        id: "pages",
        renderParameters: {
          minVal: 1,
          maxVal: 50,
          minLabel: "1",
          maxLabel: "50",
        },
        title:
          "How many pages do you estimate we'll need to design / develop? Note: Page templates count as one page. For example, a 'Product' page template could be used for each product you offer.",
        type: "range",
        defaultValue: 1,
        currentValue: 0,
        isHidden: false,
        reference: null,
      },
      {
        id: "copywriting",
        renderParameters: {
          options: [
            {
              label: "Yes, we need you to write the copy for the pages.",
              value: 1500,
            },
            {
              label: "No, we've got copywriting handled.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title: "Are we creating all the copywriting for these pages?",
        defaultValue: 0,
        isHidden: false,
        reference: null,
      },
      {
        id: "customWebGraphics",
        renderParameters: {
          options: [
            {
              label: "Yes, I'd love you to create illustrations for each page.",
              value: 1500,
            },
            {
              label:
                "No, we'll be providing photography or using stock graphics/photos.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title: "Will the pages require us to create custom graphics?",
        defaultValue: 0,
        isHidden: false,
        reference: null,
      },
      {
        id: "animatedGraphics",
        renderParameters: {
          options: [
            {
              label: "Yes, animated graphics look REALLY good.",
              value: 1000,
            },
            {
              label: "No, we're good with standard illustration.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title: (
          <>
            Do you want those graphics to be animated? (Check out our client{" "}
            <a
              href="https://www.guardtime-federal.com/"
              target="_blank"
              className="text-blue-500 cursor-pointer hover:underline hover:text-blue-700"
            >
              Guardtime Federal
            </a>
            's homepage)
          </>
        ),
        defaultValue: null,
        reference: "customWebGraphics",
        isHidden: true,
      },
      {
        id: "customWork",
        renderParameters: {
          options: [
            {
              label:
                "Yes, we'd like our website to tie into our other systems.",
              value: 1500,
            },
            {
              label: "No, our website will be mainly lead generating / static.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title:
          "Are we building any advanced functionality? Example: Integrating with an ERP, or dynamically displaying data from a database",
        defaultValue: 0,
        isHidden: false,
        reference: null,
      },
    ],
  },
  {
    id: "Report_Design",
    title: "Design",
    subtitle:
      "If you don't spend on ads now, just test out a number, 10% of your total marketing budget is a good place to start. Once you've entered all other metrics, come back to budget to see how it affects profit.",
    heading: "Projected Monthly Budget",

    formula: ({ numberOfPages, customGraphics, styleGuide, copyRight }) => {
      const pagesPrice = numberOfPages * 90
      const result =
        pagesPrice + customGraphics * 540 + styleGuide + pagesPrice * copyRight

      return result
    },
    inputs: [
      {
        id: "numberOfPages",
        renderParameters: {
          minVal: 1,
          maxVal: 400,
          minLabel: "1",
          maxLabel: "400",
        },
        title: "Number of pages:",
        type: "range",
        defaultValue: 1,
        isHidden: false,
        reference: null,
        skipValue: 25,
      },
      {
        id: "customGraphics",
        renderParameters: {
          minVal: 1,
          maxVal: 400,
          minLabel: "1",
          maxLabel: "400",
        },
        title:
          "How many custom graphics will we need to create? (These are graphics we need to custom design and that can't be found via stock sites or provided by client).",
        type: "range",
        defaultValue: 1,
        isHidden: false,
        reference: null,
        skipValue: 25,
      },
      {
        id: "styleGuide",
        renderParameters: {
          options: [
            {
              label:
                "Yes, we need you to create an overall style for the report.",
              value: 1080,
            },
            {
              label: "No, we have a defined style guide for you to use.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title:
          "Are we creating the overall style of the report or is there already a defined style guide?",
        defaultValue: 1080,
        isHidden: false,
        reference: null,
      },
      {
        id: "copyRight",
        renderParameters: {
          options: [
            {
              label:
                "Yes, we need to comply with Section 508 Accessibility Standards.",
              value: 0.25,
            },
            {
              label: "No, we don't need to have 508 compliance.",
              value: 0,
            },
          ],
        },
        type: "radio",
        title: "Do you need us to make the document 508 compliant?",
        defaultValue: 0,
        isHidden: false,
        reference: null,
      },
    ],
  },
  {
    id: "Video_Production",
    title: "Video",
    subtitle:
      "If you don't spend on ads now, just test out a number, 10% of your total marketing budget is a good place to start. Once you've entered all other metrics, come back to budget to see how it affects profit.",
    heading: "Projected Monthly Budget",
    formula: ({ minuteOfVideo, quality, travellingRequired }) => {
      const withQuality = minuteOfVideo * quality
      if (travellingRequired === 1) {
        const withTravelling = (minuteOfVideo / 4) * 3000
        return withQuality + withTravelling
      }
      return withQuality
    },
    inputs: [
      {
        id: "minuteOfVideo",
        renderParameters: {
          minVal: 1,
          maxVal: 25,
          minLabel: "1",
          maxLabel: "25",
        },
        title: "How many minutes will the video be?",
        type: "range",
        defaultValue: 1,
        currentValue: 0,
        isHidden: false,
        reference: null,
      },
      {
        id: "quality",
        renderParameters: {
          options: [
            {
              label: "I'm not looking for anything crazy—just professional.",
              value: 1200,
            },
            {
              label:
                "I don't need Hollyood, but it should be pretty cinematic.",
              value: 2000,
            },
            {
              label: "I want Hollywood level of production",
              value: 5000,
            },
          ],
        },
        type: "radio",
        title: "How polished do you want the video to be?",
        defaultValue: 1200,
        isHidden: false,
        reference: null,
      },
      {
        id: "travellingRequired",
        renderParameters: {
          options: [
            {
              label: "Yes",
              value: 1,
            },
            {
              label: "No",
              value: 0,
            },
          ],
        },
        type: "radio",
        title: "Will travel outside of Denver metro be required?",
        defaultValue: 0,
        isHidden: false,
        reference: null,
      },
    ],
  },
  {
    id: "Animation",
    title: "Animation",
    subtitle:
      "If you don't spend on ads now, just test out a number, 10% of your total marketing budget is a good place to start. Once you've entered all other metrics, come back to budget to see how it affects profit.",
    heading: "Projected Monthly Budget",
    formula: ({ minutesOfAnimation, detailed }) => {
      const result = minutesOfAnimation * detailed
      return result
    },
    inputs: [
      {
        id: "minutesOfAnimation",
        renderParameters: {
          minVal: 1,
          maxVal: 25,
          minLabel: "1",
          maxLabel: "25",
        },
        title: "How many minutes will the video be?",
        type: "range",
        defaultValue: 1,
        currentValue: 1,
        isHidden: false,
        reference: null,
      },
      {
        id: "detailed",
        renderParameters: {
          options: [
            {
              label:
                "Fair amount of attention to detail, but we aren't going to nit-pick ",
              value: 2500,
            },
            {
              label:
                "Very detailed oriented, we need to make sure all the small details are right",
              value: 4000,
            },
          ],
        },
        type: "radio",
        title: "How detail oriented does the animation need to be?",
        defaultValue: 2500,
        isHidden: false,
        reference: null,
      },
    ],
  },
  // {
  //   id: "eCommerce_Marketing",
  //   booked: true,
  //   title: "How much do you spend a month on digital ads?",
  //   subtitle:
  //     "If you don't spend on ads now, just test out a number, 10% of your total marketing budget is a good place to start. Once you've entered all other metrics, come back to budget to see how it affects profit.",
  //   heading: "Projected Monthly Budget",
  //   pricePerMonth: true,
  //   formula: ({ revenue, percentage }) => {
  //     const revenueInMillion = revenue * 1000000
  //     const per = percentage / 100
  //     const result = (revenueInMillion * per) / 12
  //     return result
  //   },
  //   inputs: [
  //     {
  //       id: "revenue",
  //       renderParameters: {
  //         minVal: 1,
  //         maxVal: 50,
  //         minLabel: "$1M",
  //         maxLabel: "$50M",
  //         helperText: "M",
  //         size: "small",
  //       },
  //       title: "Annual Revenue (in Millions):",
  //       type: "range",
  //       defaultValue: 1,
  //       isHidden: false,
  //       reference: null,
  //     },
  //     {
  //       id: "percentage",
  //       renderParameters: {
  //         minVal: 2,
  //         maxVal: 10,
  //         minLabel: "2%",
  //         minLabelHelperText: "Solid Growth",
  //         maxLabel: "10%",
  //         maxLabelHelperText: "Rapid Growth",
  //         size: "small",
  //       },
  //       title: "Revenue to Spend on Marketing:",
  //       type: "range",
  //       defaultValue: 2,
  //       isHidden: false,
  //       reference: null,
  //     },
  //   ],
  //   description: (
  //     <>
  //       <p>
  //         Our eCommerce marketing can include everything from ads management,
  //         SEO, product page updates, newsletters, promotions, design updates,
  //         and more.
  //       </p>
  //       <br />
  //       <p>
  //         This estimate does not include advertising costs, which usually run
  //         10% - 20% of revenue for eCommerce sales.
  //       </p>
  //     </>
  //   ),
  // },
]
